import * as React from 'react';
import { ReactNode, useContext } from 'react';
import { PersonApi, SolrPersonReduced } from '../../../api/core';
import { SearchResult, SearchResultHeader } from './SearchResult';
import { BreakingList } from '../../detail/components/layout/BreakingList';
import { ExternalId } from '../../detail/components/ExternalId';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import { getAuthMiddleware } from '../../../Auth/utils/GetAuthMiddleware';
import { ToastContext, ToastContextType } from '../../../util/providers/ToastProvider';
import { IntlShape, useIntl } from 'react-intl';
import { NavigateFunction, useNavigate } from 'react-router';
import { deletePerson, requestDeletePerson } from '../../../util/actions/person-actions';
import { InternalLink } from '../../components/InternalLink';
import { OrganisationHierarchyToolTip } from '../../components/OrganisationHierarchyToolTip';
import { ProjectHierarchyToolTip } from '../../components/ProjectHierarchyToolTip';


export interface SearchResultPersonProps {
  key: number;
  position: number;
  result: SolrPersonReduced;
  redirectPath?: string;
}

export function SearchResultPerson(props: SearchResultPersonProps): ReactNode {
  const {actions} = useContext(AuthContext);
  const personApi: PersonApi = new PersonApi().withMiddleware(getAuthMiddleware(actions));
  const toast: ToastContextType = useContext(ToastContext);
  const intl: IntlShape = useIntl();
  const navigate: NavigateFunction = useNavigate();
  const {result} = props;

  const header: SearchResultHeader = {
    label: result.name.join(', '),
    url: `/person/${result.id}`,
  };

  const contents: ReactNode[] = [];
  if ((result.orcid && result.orcid.length > 0) || (result.gnd && result.gnd.length > 0)) {
    contents.push(
      <BreakingList separator={'  '}>
        {
          [
            ...(result.orcid || []).map((externalId: string, index: number): ReactNode => (
              <div key={`orcid${index}`}><ExternalId id={{externalId, type: 'orcid'}} entity={'person'}/></div>
            )),
            ...(result.gnd || []).map((externalId: string, index: number): ReactNode => (
              <div key={`gnd${index}`}><ExternalId id={{externalId, type: 'gnd'}} entity={'person'}/></div>
            )),
          ]
        }
      </BreakingList>,
    );
  }
  if (result.organisations && result.organisations.length > 0) {
    contents.push(
      <BreakingList separator={'; '}>
        {result.organisations.map((organisation: string, idx: number): ReactNode => {
          const split: string[] = organisation.split('#', 2);
          return (
            <div key={idx}>
              <OrganisationHierarchyToolTip idx={`PO${props.result.id}I${idx}`} id={split[0]}>
                <InternalLink to={`/organisation/${split[0]}`}>
                  {split[1]}
                </InternalLink>
              </OrganisationHierarchyToolTip>
            </div>
          );
        })}
      </BreakingList>,
    );
  }
  if (result.projects && result.projects.length > 0) {
    contents.push(
      <BreakingList separator={'; '}>
        {result.projects.map((project: string, idx: number): ReactNode => {
          const split: string[] = project.split('#', 2);
          return (
            <div key={idx}>
              <ProjectHierarchyToolTip idx={`PP${props.result.id}I${idx}`} id={split[0]}>
                <InternalLink to={`/project/${split[0]}`}>
                  {split[1]}
                </InternalLink>
              </ProjectHierarchyToolTip>
            </div>
          );
        })}
      </BreakingList>,
    );
  }

  return <SearchResult
    position={props.position}
    header={header}
    contents={contents}
    result={result}
    requestDelete={(): void => requestDeletePerson(personApi, toast, intl, navigate, +result.id, result.name[0], props.redirectPath)}
    delete={(): void => deletePerson(personApi, toast, intl, navigate, +result.id, result.name[0], props.redirectPath)}
    editUrl={`/person/${result.id}/edit`}
  />;
}
